import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import axios from '../../utils/axios';
import { dispatch } from '../store';

export enum STATUS {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export enum PRIORITY {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type Employee = {
  id: number;
  name: string;
  code: string;
  internal_org_id: number;
};

export type Tasks = {
  id?: number;
  name: string;
  start_at: string;
  end_at: string;
  status: STATUS;
  employee_id?: number | null;
  employee?: Employee | null;
  note?: string;
  priority?: PRIORITY;
  serial?: number;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  created_by?: number | null;
  updated_by?: number | null;
};

export type DataPaging = {
  currentPage: number;
  totalItem: number;
  rows_per_page: number;
};

export type TargetOKRState = {
  isLoading: boolean;
  error: Record<string, unknown> | null;
  targets: Tasks[];
  dataPaging: DataPaging | null;
  targetByIdData: Tasks | null;
  statistics: Sumary;
  statisticsWeek: Sumary;
  statisticsMonth: Sumary;
};

export type Sumary = {
  completed: number;
  not_started: number;
  in_progress: number;
};

const initialState: TargetOKRState = {
  isLoading: false,
  error: null,
  targets: [],
  statistics: {
    completed: 0,
    not_started: 0,
    in_progress: 0,
  },
  statisticsWeek: {
    completed: 0,
    not_started: 0,
    in_progress: 0,
  },
  statisticsMonth: {
    completed: 0,
    not_started: 0,
    in_progress: 0,
  },
  dataPaging: null,
  targetByIdData: null,
};

const slice = createSlice({
  name: 'targetOKR',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action: PayloadAction<Record<string, unknown>>) {
      state.isLoading = false;
      state.error = action.payload;
      throw new Error(state.error?.message as string);
    },
    getTargetSuccess(state, action: PayloadAction<Tasks[]>) {
      state.isLoading = false;
      state.targets = action.payload;
    },
    getDataPaging(state, action: PayloadAction<DataPaging>) {
      state.isLoading = false;
      state.dataPaging = action.payload;
    },
    startLoadingById(state) {
      state.isLoading = true;
    },
    getTargetOKRById(state, action: PayloadAction<Tasks>) {
      state.isLoading = false;
      state.targetByIdData = action.payload;
    },
    updateStatus(state, action: PayloadAction<{ id: number; status: STATUS }>) {
      const { id, status } = action.payload;
      const target = state.targets.find((item) => item.id === id);
      if (target) {
        target.status = status;
      }
    },
    getStatistics(state, action: PayloadAction<{ completed: number; not_started: number; in_progress: number }>) {
      state.statistics = action.payload;
    },
    getStatisticsWeek(state, action: PayloadAction<{ completed: number; not_started: number; in_progress: number }>) {
      state.statisticsWeek = action.payload;
    },
    getStatisticsMonth(state, action: PayloadAction<{ completed: number; not_started: number; in_progress: number }>) {
      state.statisticsMonth = action.payload;
    },
  },
});

export default slice.reducer;

// Actions
export const { getTargetSuccess, updateStatus } = slice.actions;

export interface TargetQueryParams {
  internal_org_id?: number;
  employee_ids?: number[];
  start_at_from?: string | null;
  start_at_to?: string | null;
  rows_per_page?: number;
  current_page?: number;
  status?: STATUS;
}

const defaultSumary = {
  completed: 0,
  not_started: 0,
  in_progress: 0,
};

export function getTarget(queryData?: TargetQueryParams) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('tasks', { params: { ...queryData, sort_by: 'created_at' } });
      const tasksResponse = response?.data?.data as {
        current_page: number;
        data: Tasks[];
        per_page: string;
        status_summary: Sumary;
        total: number;
      };

      let taskList: Tasks[] = tasksResponse?.data ?? [];
      if ('per_page' in tasksResponse) {
        taskList = tasksResponse.data;
      }

      dispatch(slice.actions.getTargetSuccess(taskList));
      dispatch(slice.actions.getStatistics(tasksResponse?.status_summary || defaultSumary));
      if (tasksResponse?.total > 0) {
        dispatch(
          slice.actions.getDataPaging({
            currentPage: tasksResponse.current_page,
            rows_per_page: +tasksResponse.per_page,
            totalItem: tasksResponse.total,
          })
        );
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}
export function getTargetWeek(
  queryData?: Omit<TargetQueryParams, 'rows_per_page' | 'current_page' | 'start_at_to' | 'start_at_from'>
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const startAt = moment().subtract(7, 'days').format('YYYY-MM-DD');
      const endAt = moment().format('YYYY-MM-DD');
      const response = await axios.get('tasks', {
        params: { ...queryData, start_at_from: startAt, start_at_to: endAt, sort_by: 'created_at' },
      });
      const tasksResponse = response?.data?.data;
      dispatch(slice.actions.getStatisticsWeek(tasksResponse?.status_summary || defaultSumary));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}
export function getTargetMonth(
  queryData?: Omit<TargetQueryParams, 'rows_per_page' | 'current_page' | 'start_at_to' | 'start_at_from'>
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const startAt = moment().subtract(30, 'days').format('YYYY-MM-DD');
      const endAt = moment().format('YYYY-MM-DD');
      const response = await axios.get('tasks', {
        params: { ...queryData, start_at_from: startAt, start_at_to: endAt, sort_by: 'created_at' },
      });
      const tasksResponse = response?.data?.data;
      dispatch(slice.actions.getStatisticsMonth(tasksResponse?.status_summary || defaultSumary));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}

export function getTargetOKRById(id: number) {
  return async () => {
    dispatch(slice.actions.startLoadingById());
    try {
      const response = await axios.get(`tasks/${id}`);
      const targetSample = response?.data?.data ?? null;
      dispatch(slice.actions.getTargetOKRById(targetSample));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}

export function createTask(
  taskData: Omit<Tasks, 'id' | 'created_at' | 'updated_at' | 'serial'>,
  queryData?: TargetQueryParams
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post('tasks', taskData);
      dispatch(getTarget(queryData));
      dispatch(getTargetWeek(queryData));
      dispatch(getTargetMonth(queryData));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}

export function updateTask(
  id: number,
  taskData: Partial<Omit<Tasks, 'id' | 'created_at' | 'updated_at' | 'serial'>>,
  queryData?: TargetQueryParams
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.put(`tasks/${id}`, taskData);
      dispatch(getTarget(queryData));
      dispatch(getTargetWeek(queryData));
      dispatch(getTargetMonth(queryData));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}

export function deleteTask(id: number, queryData?: TargetQueryParams) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`tasks/${id}`);
      dispatch(getTarget(queryData));
      dispatch(getTargetWeek(queryData));
      dispatch(getTargetMonth(queryData));
    } catch (error) {
      dispatch(slice.actions.hasError(error as Record<string, unknown>));
    }
  };
}
